import type { DropdownMenuItem } from '@nuxt/ui';
import type { ComputedRef, Reactive } from 'vue';
import type { DetailPageContext } from '#layers/block-engine/public';
import type { CompositePrimaryKey, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#app/composables/router';
import { CrudAction, useAuthorization } from '#layers/access/public';
import { useQueryApi } from '#layers/data-model/public';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { useCollectionRoutes } from '#layers/spaces/public';
import { showConfirmationDialog, useToastResultHandling } from '#layers/ui/public';

export interface UseFormBlockOverflowActionsParams {
	isCreate: ComputedRef<boolean>;
	collectionData: ComputedRef<ResolvedMonospaceCollection | undefined>;
	parsedFinalItemId: ComputedRef<CompositePrimaryKey | null>;
	detailPageContext: Reactive<DetailPageContext & { isDetailPage: boolean }>;
}

export interface UseFormBlockOverflowActionsReturn {
	overflowActions: ComputedRef<DropdownMenuItem[]>;
}

export function useFormBlockOverflowActions(params: UseFormBlockOverflowActionsParams): UseFormBlockOverflowActionsReturn {
	const { isCreate, collectionData, parsedFinalItemId, detailPageContext } = params;

	const store = useWorkspaceEntityStore();
	const router = useRouter();
	const { t } = useI18n();
	const { can } = useAuthorization();
	const { listingRoute } = useCollectionRoutes(collectionData);
	const { onSuccess: deleteSuccess, onError: deleteError } = useToastResultHandling({
		success: {
			title: t('pages.content.delete_item.success'),
		},
		error: {
			title: t('pages.content.delete_item.error'),
		},
	});

	const supportsDelete = useQueryApi(collectionData).supports((api) => api.deleteOne());

	const canDeleteItem = computed(() =>
		collectionData.value && supportsDelete.value
			? can({ collection: collectionData.value, action: CrudAction.Delete, id: parsedFinalItemId.value ?? undefined })
			: false,
	);

	const overflowActions = computed(() => {
		const actions: DropdownMenuItem[] = [];

		if (!isCreate.value && !detailPageContext.pageDisplayMode && canDeleteItem.value && detailPageContext.allowDelete !== false) {
			// only add delete action if the item exists and the page is not shown in a drawer
			// TODO this needs a better indicator. A page might be shown in a modal but we still want to show this
			actions.push({
				label: t('pages.content.delete_item.action'),
				color: 'error',
				onSelect: async () => {
					if (!collectionData.value) return;

					const storeApi = store.$collection(collectionData.value.apiName);
					const key = storeApi.getKey(parsedFinalItemId.value);

					if (!key) return;

					const confirmed = await showConfirmationDialog({
						props: {
							title: t('pages.content.delete_item.title'),
							description: t('pages.content.delete_item.description'),
							confirm: {
								color: 'error',
								label: t('pages.content.delete_item.confirm'),
							},
						},
					});

					if (!confirmed) return;

					try {
						await storeApi?.delete(key);

						deleteSuccess();

						if (listingRoute.value) {
							router.replace(listingRoute.value);
						}
					}
					catch (error) {
						deleteError(error);
					}
				},
			} as const);
		}

		return actions;
	});

	return {
		overflowActions,
	};
}
